import { DateTime } from "luxon";
import React, { CSSProperties, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	ReleaseChannel,
	DFUImage,
	DFURequest,
	parseDFUName,
	VelavuAPI,
	VelavuDeviceVesta,
	VelavuStreamUpdateOperation,
	VelavuStreamUpdateTarget,
} from "velavu-js-api";
import IconClose from "../../dynamicicons/icon-close";
import IconDownload from "../../dynamicicons/icon-download";
import VelavuCard from "../../elements/velavu-card";
import VelavuExpandMenu from "../../elements/velavu-expand-menu";
import { useVelavuModal } from "../../elements/velavu-modal";
import { useCancellableEffect } from "../../helper/hook-helper";
import {
	mapReleaseChannel,
	mapDFURequestStatus,
} from "../../helper/language-helper";
import { StatusType } from "../../helper/status-helper";
import * as globals from "../../styles/global.icss.scss";
import TabTagDFUInstallModal from "./tab-tag-dfu-install-modal";
import * as styles from "./tab-tag-dfu.module.scss";
import { VelavuRoundTable } from "../../elements/velavu-round-table";
import { useVelavuStream } from "velavu-react";
import VelavuSelect from "../../elements/velavu-select";
import { classArr } from "../../helper/style-helper";
import { compareVersionsStr } from "../../helper/version-helper";
import { useHasPermission } from "../sign-in-context/sign-in-context";

const displayLimitDefault = 2;
const displayLimitMore = 4;

function ImageTableRow(props: {
	image: DFUImage;
	onInstall?: (image: DFUImage) => void;
}) {
	const { t } = useTranslation();

	const handleInstall = useCallback(() => {
		props.onInstall?.(props.image);
	}, [props.onInstall, props.image]);

	let channel: string;
	if (props.image.channel !== undefined) {
		channel = mapReleaseChannel(props.image.channel, t);
	} else {
		channel = t("asset_detail.dfu.channel_none");
	}

	return (
		<tr
			className={classArr(
				props.onInstall !== undefined && styles.tableActionRow,
			)}
			onClick={props.onInstall !== undefined ? handleInstall : undefined}
		>
			<td>{props.image.version}</td>
			<td>{channel}</td>
			<td>
				{DateTime.fromISO(props.image.release_date).toLocaleString(
					DateTime.DATE_MED,
				)}
			</td>
			<td>{props.image.comment}</td>
			<td>
				{props.onInstall !== undefined && (
					<IconDownload
						className={styles.hoverIcon}
						color={globals.textSecondary}
					/>
				)}
			</td>
		</tr>
	);
}

function RequestTableRow(props: {
	request: DFURequest;
	onRemove?: (requestID: string) => void;
}) {
	const { t } = useTranslation();

	const handleRemove = useCallback(() => {
		props.onRemove?.(props.request.id);
	}, [props.onRemove, props.request.id]);

	return (
		<tr
			className={classArr(
				props.onRemove !== undefined && styles.tableActionRow,
			)}
			onClick={props.onRemove !== undefined ? handleRemove : undefined}
		>
			<td>{parseDFUName(props.request.data.name).version}</td>
			<td>
				{DateTime.fromISO(props.request.timestamp).toLocaleString(
					DateTime.DATETIME_MED,
				)}
			</td>
			<td>{mapDFURequestStatus(props.request.data.status, t)}</td>
			<td>
				{props.onRemove !== undefined && (
					<IconClose
						className={styles.hoverIcon}
						color={globals.textSecondary}
					/>
				)}
			</td>
		</tr>
	);
}

export default function TabTagDFU(props: {
	style?: CSSProperties;
	className?: string;
	tag: VelavuDeviceVesta;
}) {
	const { t } = useTranslation();

	const canWriteDFU = useHasPermission("dfu:write");
	const canWriteDevices = useHasPermission("devices:write");

	const tagID = props.tag.id;
	const tagVersion = useMemo(() => {
		if (props.tag.state?.app_version === undefined) {
			return null;
		}

		try {
			return parseDFUName(props.tag.state.app_version).version;
		} catch (error) {
			return null;
		}
	}, [props.tag.state?.app_version]);

	//Images (newest to oldest)
	const [images, setImages] = useState<DFUImage[] | undefined>(undefined);
	useCancellableEffect(
		(addPromise) => {
			addPromise(VelavuAPI.dfu.getDFUImages(tagID)).then(setImages);
		},
		[tagID, setImages],
	);
	const [displayLimitImages, setDisplayLimitImages] =
		useState(displayLimitDefault);
	const increaseDisplayLimitImages = useCallback(
		() => setDisplayLimitImages((limit) => limit + displayLimitMore),
		[setDisplayLimitImages],
	);
	//If we're only hiding one item under "show more", we're not saving space, so we might as well show it
	const adjustedDisplayLimitImages = useMemo(
		() =>
			displayLimitImages + 1 === images?.length
				? images.length
				: displayLimitImages,
		[displayLimitImages, images],
	);

	//Requests (newest to oldest)
	const [requests, setRequests] = useState<DFURequest[] | undefined>(
		undefined,
	);

	//Fetch requests on load
	useCancellableEffect(
		(addPromise) => {
			setRequests(undefined);
			addPromise(VelavuAPI.dfu.getDFURequests(tagID)).then(setRequests);
		},
		[tagID, setRequests],
	);

	//Subscribe to live updates
	useVelavuStream(
		VelavuStreamUpdateTarget.Event,
		useCallback(
			(update) => {
				//Ignore if this update isn't about an updated DFU request
				if (
					update.operation !== VelavuStreamUpdateOperation.Updated ||
					update.body.category !== "DFU_REQUEST"
				) {
					return;
				}
				const dfuRequest = update.body;

				setRequests((prevRequests) => {
					//Don't do anything if requests haven't loaded yet
					if (prevRequests === undefined) {
						return undefined;
					}

					//Check if the update concerns one of our DFU requests
					const index = prevRequests.findIndex(
						(request) => request.id === update.id,
					);
					if (index === -1) {
						return prevRequests;
					}

					//Update the request in the list
					const newRequests = [...prevRequests];
					newRequests[index] = dfuRequest;
					return newRequests;
				});
			},
			[setRequests],
		),
	);

	const [displayLimitRequests, setDisplayLimitRequests] =
		useState(displayLimitDefault);
	const increaseDisplayLimitRequests = useCallback(
		() => setDisplayLimitRequests((limit) => limit + displayLimitMore),
		[setDisplayLimitRequests],
	);
	const adjustedDisplayLimitRequests = useMemo(
		() =>
			displayLimitRequests + 1 === requests?.length
				? requests.length
				: displayLimitRequests,
		[displayLimitRequests, requests],
	);

	//Suggest the user to update to a newer version
	const updateSuggestion = useMemo(() => {
		//Wait for data to load
		if (images === undefined || requests === undefined) return false;

		//Don't suggest an upgrade if there are no images available
		if (images.length === 0) return false;

		//Get the latest image
		const latestImage = images[0];

		//If we've already put in a request, don't suggest an upgrade
		if (requests.length > 0 && requests[0].data.name === latestImage.name) {
			return false;
		}

		//If we can't determine the current version, don't suggest an upgrade
		if (tagVersion === null) return false;

		//If we're already on the latest version or newer, don't suggest an upgrade
		if (compareVersionsStr(latestImage.version, tagVersion) <= 0) {
			return false;
		}

		//Suggest to upgrade to the latest image
		return true;
	}, [images, requests, tagVersion]);
	const updateSuggestionFirmware = images?.[0];

	const { pushModal } = useVelavuModal();
	const onInstallFirmware = useCallback(
		(image: DFUImage) => {
			pushModal((resolve) => (
				<TabTagDFUInstallModal
					tag={props.tag}
					image={image}
					onConfirm={() => resolve(true)}
					onCancel={() => resolve(false)}
				/>
			))
				.then((result) => {
					if (!result) throw new Error("User cancelled request");

					//Put in a request
					return VelavuAPI.dfu.createDFURequest(
						props.tag.id,
						image.name,
					);
				})
				.then((request: DFURequest) => {
					//Update the request list
					setRequests((requests) =>
						requests === undefined
							? undefined
							: [request].concat(requests),
					);
				})
				.catch(console.log);
		},
		[setRequests, pushModal, props.tag],
	);

	const onDeleteRequest = useCallback(
		(requestID: string) => {
			VelavuAPI.dfu.deleteDFURequest(requestID);

			setRequests((requests) => {
				if (requests === undefined) return undefined;

				const index = requests.findIndex(
					(request) => request.id === requestID,
				);
				if (index === -1) return requests;

				const newRequests = [...requests];
				newRequests.splice(index, 1);
				return newRequests;
			});
		},
		[setRequests],
	);

	const [dfuChannel, setDFUChannel] = useState<ReleaseChannel | "disabled">(
		props.tag.dfu_channel ?? "disabled",
	);
	const onChangeDFUChannel = useCallback(
		(value: ReleaseChannel | "disabled") => {
			setDFUChannel(value);
			if (value === "disabled") {
				VelavuAPI.devices
					.removeSpecificDeviceDFUChannel(tagID)
					.catch(console.error);
			} else {
				VelavuAPI.devices
					.modifySpecificDevice<VelavuDeviceVesta>(tagID, {
						dfu_channel: value,
					})
					.catch(console.error);
			}
		},
		[tagID],
	);

	const handleInstallSuggestion = useCallback(() => {
		if (updateSuggestionFirmware !== undefined) {
			onInstallFirmware(updateSuggestionFirmware);
		}
	}, [updateSuggestionFirmware, onInstallFirmware]);

	return (
		<VelavuExpandMenu
			style={props.style}
			className={props.className}
			label={t("asset_detail.dfu.title")}
			indicator={updateSuggestion ? StatusType.blue : undefined}
		>
			<span className={styles.tableTitle}>
				{t("asset_detail.dfu.automatic_title")}
			</span>

			{updateSuggestion && updateSuggestionFirmware !== undefined && (
				<VelavuCard
					className={classArr(styles.itemMargin, styles.updateCard)}
					type={StatusType.blue}
					selectable={canWriteDFU}
					onClick={canWriteDFU ? handleInstallSuggestion : undefined}
				>
					<div className={styles.updateCardText}>
						<div className={styles.updateCardTextTitle}>
							{t("asset_detail.dfu.update_available", {
								version: updateSuggestionFirmware.version,
							})}
						</div>
						<div
							className={styles.updateCardTextBody}
						>{`${DateTime.fromISO(
							updateSuggestionFirmware.release_date,
						).toLocaleString(DateTime.DATE_MED)} • ${
							updateSuggestionFirmware.comment
						}`}</div>
					</div>

					{canWriteDFU && (
						<IconDownload color={globals.textPrimary} />
					)}
				</VelavuCard>
			)}

			<div className={classArr(styles.itemMargin, styles.channelCard)}>
				<span className={styles.channelLabel}>
					{t("asset_detail.dfu.channel_help")}
				</span>
				<VelavuSelect
					className={classArr(styles.channelSelect)}
					fullWidth
					value={dfuChannel}
					onChange={onChangeDFUChannel}
					disabled={!canWriteDevices}
				>
					<option value="disabled">
						{t("asset_detail.dfu.disable_automatic")}
					</option>
					<option value={ReleaseChannel.Stable}>
						{mapReleaseChannel(ReleaseChannel.Stable, t)}
					</option>
					<option value={ReleaseChannel.Beta}>
						{mapReleaseChannel(ReleaseChannel.Beta, t)}
					</option>
				</VelavuSelect>
			</div>

			{images !== undefined && images.length > 0 && (
				<>
					<span className={styles.tableTitle}>
						{t("asset_detail.dfu.available_releases")}
					</span>

					<VelavuRoundTable
						className={styles.itemMargin}
						layout={[
							{ flex: 15 },
							{ flex: 20 },
							{ flex: 30 },
							{ flex: 50 },
							{ width: 24 },
						]}
					>
						<thead>
							<tr>
								<th>{t("asset_detail.dfu.table.version")}</th>
								<th>{t("asset_detail.dfu.table.channel")}</th>
								<th>
									{t("asset_detail.dfu.table.release_date")}
								</th>
								<th>{t("asset_detail.dfu.table.notes")}</th>
								<th
									aria-label={t(
										"asset_detail.dfu.table.actions",
									)}
								></th>
							</tr>
						</thead>

						<tbody>
							{images
								.slice(0, adjustedDisplayLimitImages)
								.map((image) => (
									<ImageTableRow
										key={image.name}
										image={image}
										onInstall={
											canWriteDFU
												? onInstallFirmware
												: undefined
										}
									/>
								))}
						</tbody>

						{images.length > adjustedDisplayLimitImages && (
							<tfoot>
								<tr
									className={styles.rowShowMore}
									onClick={increaseDisplayLimitImages}
								>
									<td colSpan={5}>
										<span>{t("common.show_more")}</span>
									</td>
								</tr>
							</tfoot>
						)}
					</VelavuRoundTable>
				</>
			)}

			{requests !== undefined && requests.length > 0 && (
				<>
					<span className={styles.tableTitle}>
						{t("asset_detail.dfu.update_tasks")}
					</span>

					<VelavuRoundTable
						className={styles.itemMargin}
						layout={[
							{ flex: 15 },
							{ flex: 35 },
							{ flex: 50 },
							{ width: 24 },
						]}
					>
						<thead>
							<tr>
								<th>{t("asset_detail.dfu.table.version")}</th>
								<th>
									{t("asset_detail.dfu.table.install_date")}
								</th>
								<th>{t("asset_detail.dfu.table.status")}</th>
								<th
									aria-label={t(
										"asset_detail.dfu.table.actions",
									)}
								></th>
							</tr>
						</thead>

						<tbody>
							{requests
								.slice(0, adjustedDisplayLimitRequests)
								.map((request) => (
									<RequestTableRow
										key={request.id}
										request={request}
										onRemove={
											canWriteDFU
												? onDeleteRequest
												: undefined
										}
									/>
								))}
						</tbody>

						{requests.length > adjustedDisplayLimitRequests && (
							<tfoot>
								<tr
									className={styles.rowShowMore}
									onClick={increaseDisplayLimitRequests}
								>
									<td colSpan={4}>
										<span>{t("common.show_more")}</span>
									</td>
								</tr>
							</tfoot>
						)}
					</VelavuRoundTable>
				</>
			)}
		</VelavuExpandMenu>
	);
}
